import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import { modeOf } from './console/host.ts';
import { startApp } from './pwa/app.ts';

declare global {
  interface ImportMeta { env?: { DEV?: boolean } }
}

const root = document.getElementById('root');
if (!root) throw new Error('index.html must contain <div id="root">');

// One image, two applications: the address decides which one this page is (console/host.ts). Each
// is its own chunk, imported only here, so the console never loads the workspace shell's code,
// routes or session, and a workspace never loads the console's.
const mode = modeOf(window.location, import.meta.env?.DEV === true);

if (mode.app === 'console') {
  document.documentElement.setAttribute('data-console', '');
  // The console is never an installed app: no manifest is linked here (index.html) and no worker
  // may run, so one left behind on this address is removed.
  void navigator.serviceWorker?.getRegistrations().then((all) => all.forEach((r) => void r.unregister())).catch(() => {});
  void import('./console/Console.tsx').then(({ Console }) => {
    createRoot(root).render(<StrictMode><Console prefix={mode.prefix} /></StrictMode>);
  });
} else {
  // The workspace shell installs as an app (pwa/app.ts). Not in development: Vite serves no worker,
  // and a worker left from a build would answer for the dev server.
  if (import.meta.env?.DEV !== true) startApp();
  void Promise.all([import('./App.tsx'), import('./analytics.ts')]).then(([{ App }, { startAnalytics }]) => {
    // Product analytics, when this installation set a PostHog key; otherwise nothing is loaded at all.
    void startAnalytics(window.CORE_CONFIG);
    createRoot(root).render(<StrictMode><App /></StrictMode>);
  });
}
